import {
	AdminAddUserToGroupCommand,
	AdminCreateUserCommand,
	AdminDeleteUserCommand,
	AdminListGroupsForUserCommand,
	AdminRemoveUserFromGroupCommand,
	AdminUpdateUserAttributesCommand,
	CognitoIdentityProviderClient,
	ListUsersCommand,
	type UserType,
} from "@aws-sdk/client-cognito-identity-provider";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-providers";
import { fetchAuthSession } from "aws-amplify/auth";
import dayjs from "dayjs";
import { useCallback } from "react";
import { generatePassword } from "../lib/generate-password";
import type { AdminListData } from "../types/admin";

interface CognitoOperations {
	createUser: (data: { email: string; department?: string; role: string }) => Promise<void>;
	listUsers: () => Promise<AdminListData[]>;
	deleteUser: (email: string) => Promise<void>;
	updateUserAttributes: (data: {
		email: string;
		department?: string;
		role: string;
	}) => Promise<void>;
}

export const useCognitoOperations = (): CognitoOperations => {
	const cognitoClient = useCallback(async () => {
		const session = await fetchAuthSession();
		const idToken = session.tokens?.idToken?.toString();
		const COGNITO_IDP = `cognito-idp.${import.meta.env.VITE_AWS_REGION}.amazonaws.com/${import.meta.env.VITE_COGNITO_USERPOOL_ID}`;
		if (!idToken) {
			throw new Error("ID Token is undefined");
		}
		const loginData = {
			[COGNITO_IDP]: idToken,
		};
		return new CognitoIdentityProviderClient({
			region: import.meta.env.VITE_AWS_REGION,
			credentials: fromCognitoIdentityPool({
				clientConfig: { region: import.meta.env.VITE_AWS_REGION },
				identityPoolId: import.meta.env.VITE_COGNITO_IDPOOL_ID || "",
				logins: loginData,
			}),
		});
	}, []);

	// ユーザー作成
	const createUser = useCallback(
		async (data: { email: string; department?: string; role: string }): Promise<void> => {
			try {
				const client = await cognitoClient();
				const createUserInput = {
					UserPoolId: import.meta.env.VITE_COGNITO_USERPOOL_ID,
					Username: data.email,
					UserAttributes: [
						{
							Name: "email",
							Value: data.email,
						},
						{
							Name: "email_verified",
							Value: "true",
						},
						{
							Name: "custom:department",
							Value: data.department || "",
						},
						{
							Name: "custom:role",
							Value: data.role,
						},
					],
					TemporaryPassword: generatePassword(10), // 一時的なパスワード（ユーザーが初回ログイン時に変更）
				};
				const createUserCommand = new AdminCreateUserCommand(createUserInput);
				await client.send(createUserCommand);
				let usergroup: string;
				if (data.role === "管理者") {
					usergroup = "admins";
				} else if (data.role === "実行者") {
					usergroup = "users";
				} else {
					usergroup = "";
				}
				const addUserGroupInput = {
					UserPoolId: import.meta.env.VITE_COGNITO_USERPOOL_ID,
					Username: data.email,
					GroupName: usergroup,
				};
				const addUserGroupCommand = new AdminAddUserToGroupCommand(addUserGroupInput);
				await client.send(addUserGroupCommand);
			} catch (err) {
				throw new Error(err instanceof Error ? err.message : String(err));
			}
		},
		[cognitoClient],
	);

	// ユーザー一覧の情報取得
	const listUsers = useCallback(async (): Promise<AdminListData[]> => {
		try {
			const client = await cognitoClient();
			const listUsersCommand = new ListUsersCommand({
				UserPoolId: import.meta.env.VITE_COGNITO_USERPOOL_ID,
			});
			const getValueByAttribute = (user: UserType, attributeName: string): string => {
				const attribute = user.Attributes?.find((attr) => attr.Name === attributeName);
				return attribute ? attribute.Value || "" : "";
			};
			const formatDate = (dateString: Date | undefined): string => {
				if (!dateString) {
					return "";
				}
				return dayjs(dateString).format("YYYY/MM/DD HH:mm");
			};
			const response = await client.send(listUsersCommand);
			if (response.Users) {
				const users = await Promise.all(
					response.Users.map(async (user: UserType) => {
						const input = {
							Username: user.Username,
							UserPoolId: import.meta.env.VITE_COGNITO_USERPOOL_ID,
						};
						const ListUserGrpCommand = new AdminListGroupsForUserCommand(input);
						const ListUserGrpResponse = await client.send(ListUserGrpCommand);
						const userGroup = ListUserGrpResponse.Groups?.map((group) => group.GroupName);
						let usergroup = "";
						if (userGroup?.includes("admins")) {
							usergroup = "管理者";
						} else if (userGroup?.includes("users")) {
							usergroup = "実行者";
						}
						return {
							id: getValueByAttribute(user, "sub"),
							username: user.Username || "",
							email: getValueByAttribute(user, "email"),
							usergroup: usergroup,
							emailVerified:
								getValueByAttribute(user, "email_verified") === "true" ? "有効" : "無効",
							role: getValueByAttribute(user, "custom:role"),
							department: getValueByAttribute(user, "custom:department"),
							enabled: user.Enabled ? "有効" : "無効",
							userStatus: user.UserStatus || "UNKNOWN",
							userCreateDate: formatDate(user.UserCreateDate),
							userLastModifiedDate: formatDate(user.UserLastModifiedDate),
						};
					}),
				);
				return users;
			}
			return [];
		} catch (err) {
			throw new Error(err instanceof Error ? err.message : String(err));
		}
	}, [cognitoClient]);

	// 削除処理
	const deleteUser = useCallback(
		async (email: string): Promise<void> => {
			try {
				const client = await cognitoClient();
				const deleteUserCommand = new AdminDeleteUserCommand({
					UserPoolId: import.meta.env.VITE_COGNITO_USERPOOL_ID,
					Username: email,
				});
				await client.send(deleteUserCommand);
			} catch (err) {
				throw new Error(err instanceof Error ? err.message : String(err));
			}
		},
		[cognitoClient],
	);

	// ユーザー情報の変更
	const updateUserAttributes = useCallback(
		async (data: { email: string; department?: string; role: string }): Promise<void> => {
			try {
				const client = await cognitoClient();
				const updateUserAttributesCommand = new AdminUpdateUserAttributesCommand({
					UserPoolId: import.meta.env.VITE_COGNITO_USERPOOL_ID,
					Username: data.email,
					UserAttributes: [
						{ Name: "custom:department", Value: data.department || "" },
						{ Name: "custom:role", Value: data.role },
					],
				});
				await client.send(updateUserAttributesCommand);
				let usergroup: string;
				if (data.role === "管理者") {
					usergroup = "admins";
				} else if (data.role === "実行者") {
					usergroup = "users";
				} else {
					usergroup = "";
				}
				const addUserGroupInput = {
					UserPoolId: import.meta.env.VITE_COGNITO_USERPOOL_ID,
					Username: data.email,
					GroupName: usergroup,
				};
				const addUserGroupCommand = new AdminAddUserToGroupCommand(addUserGroupInput);
				await client.send(addUserGroupCommand);
				const updateUserGroupCommand = new AdminRemoveUserFromGroupCommand({
					UserPoolId: import.meta.env.VITE_COGNITO_USERPOOL_ID,
					Username: data.email,
					GroupName: usergroup === "admins" ? "users" : "admins",
				});
				await client.send(updateUserGroupCommand);
			} catch (err) {
				throw new Error(err instanceof Error ? err.message : String(err));
			}
		},
		[cognitoClient],
	);

	return {
		createUser,
		listUsers,
		deleteUser,
		updateUserAttributes,
	};
};
